/**
 * SectionTabBar — Horizontal section-level tab navigation
 *
 * Renders a section heading with underline-style tabs that link to Ember routes.
 * Matches the Figma "Expanded Navigation" pattern beneath the mega-menu.
 *
 * Follows the reimagined styling convention:
 * - Active state: violet-600 text + border (matches mega-menu active pattern)
 * - Inactive state: gray-700 text with hover to violet-600 (matches mega-menu nav items)
 * - Section title uses `<Heading>` from tio-ui/typography
 * - Standard Tailwind palette only — no arbitrary hex values
 *
 * @example
 * ```gts
 * <SectionTabBar
 *   @title="Loan Relief"
 *   @tabs={{this.tabs}}
 *   @activeTabId="slr"
 * />
 * ```
 */
import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { eq } from 'tio-ui/utilities';
import { Heading } from 'tio-ui/components/typography';

export interface SectionTab {
  /** Unique identifier for the tab */
  id: string;
  /** Display label */
  label: string;
  /** Ember route to navigate to */
  route: string;
  /** Whether this tab is disabled (renders as non-clickable text) */
  isDisabled?: boolean;
}

interface SectionTabBarSignature {
  Args: {
    /** Section heading displayed on the left */
    title: string;
    /** Array of tab definitions */
    tabs: SectionTab[];
    /** ID of the currently active tab */
    activeTabId: string;
  };
  Element: HTMLDivElement;
}

export default class SectionTabBar extends Component<SectionTabBarSignature> {
  <template>
    <div
      class="bg-white border-b border-gray-200 flex items-center gap-24 pt-6 px-10"
      ...attributes
    >
      <Heading
        @level={{3}}
        class="text-lg font-semibold text-gray-700 leading-none shrink-0 whitespace-nowrap"
      >
        {{@title}}
      </Heading>
      <nav class="flex flex-1 items-center gap-14" role="tablist">
        {{#each @tabs as |tab|}}
          {{#if tab.isDisabled}}
            <span
              class="shrink-0 px-2 py-3 text-sm font-medium text-gray-400 whitespace-nowrap cursor-default"
              role="tab"
              aria-selected="false"
              aria-disabled="true"
            >
              {{tab.label}}
            </span>
          {{else if (eq @activeTabId tab.id)}}
            <LinkTo
              @route={{tab.route}}
              class="shrink-0 px-2 py-3 text-sm font-semibold text-violet-600 border-b-2 border-violet-600 whitespace-nowrap leading-none"
              role="tab"
              aria-selected="true"
            >
              {{tab.label}}
            </LinkTo>
          {{else}}
            <LinkTo
              @route={{tab.route}}
              class="shrink-0 px-2 py-3 text-sm font-medium text-gray-700 whitespace-nowrap leading-none hover:text-violet-600 transition-colors"
              role="tab"
              aria-selected="false"
            >
              {{tab.label}}
            </LinkTo>
          {{/if}}
        {{/each}}
      </nav>
    </div>
  </template>
}
